import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { differenceInYears, format, parse } from "date-fns";
import * as hijriMoment from 'moment-hijri';

export interface IbanCountry {
  code: string;
  length: number;
  name: string;
  sepa?: boolean;     // true if part of SEPA (mostly Europe + some others)
}

export class JariValidators {
  // ✅ Disallow special symbols
  static validStringRegex = /^[^*|":<>[\]{}`\\()';@&$]+$/;

  // ✅ Email format
  static emailPattern: string = '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$';

  // ✅ Saudi National ID (starts with 1 or 2, length 10)
  static saudiNationalIdPattern = /^(1|2)[0-9]{9}$/;

  // ✅ Saudi phone: starts with 5, total 9 digits
  static validPhoneNumberRegex = /^5[0-9]{8}$/;

  // ✅ Password rules: uppercase, lowercase, number, special char, length 8-16
  static validPasswordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[a-zA-Z\d@$!%*?&]{8,16}$/;

  // ✅ Arabic + English letters only, no numbers
  static englishArabicNoNumbersPattern = /^[\p{L}A-Za-z\s]+$/u;

  // ✅ Same email validation
  static validStringForEmailRegex =
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  // ✅ Check allowed image types
  static isValidPhoto(file: File): boolean {
    const allowedExtensions = ['image/png', 'image/jpg', 'image/jpeg'];
    return allowedExtensions.includes(file.type);
  }

  // ✅ SA IBAN format
  static saPattern = /^SA[0-9]{22}$/;
}

/**
 * Live state of each password rule, used to drive the hint list under a password field.
 * Every flag maps to one part of {@link JariValidators.validPasswordRegex}, so
 * `allTrue(rules)` and `Validators.pattern(validPasswordRegex)` always agree.
 */
export interface PasswordRules {
  length: boolean;
  hasCapital: boolean;
  hasSmall: boolean;
  hasNumber: boolean;
  hasSpecial: boolean;
  allowedChars: boolean;
}

/** The only special characters `validPasswordRegex` accepts. Keep in sync with the i18n hints. */
export const PASSWORD_SPECIAL_CHARS = '@$!%*?&';

/** All rules unmet — the initial state before the user types anything. */
export function emptyPasswordRules(): PasswordRules {
  return {
    length: false,
    hasCapital: false,
    hasSmall: false,
    hasNumber: false,
    hasSpecial: false,
    allowedChars: false,
  };
}

/**
 * Breaks `validPasswordRegex` down into per-rule flags so the UI can show which
 * requirement is still missing instead of a single "invalid password" message.
 */
export function evaluatePasswordRules(value: string | null | undefined): PasswordRules {
  const val = value ?? '';

  return {
    length: val.length >= 8 && val.length <= 16,
    hasCapital: /[A-Z]/.test(val),
    hasSmall: /[a-z]/.test(val),
    hasNumber: /\d/.test(val),
    hasSpecial: /[@$!%*?&]/.test(val),
    // Only meaningful once something is typed, otherwise an empty box reads as valid.
    allowedChars: val.length > 0 && /^[a-zA-Z\d@$!%*?&]+$/.test(val),
  };
}

/** Validators every password control in the portal must use, in both register and reset flows. */
export const PASSWORD_VALIDATORS: ValidatorFn[] = [
  Validators.required,
  Validators.minLength(8),
  Validators.maxLength(16),
  Validators.pattern(JariValidators.validPasswordRegex),
];

// ✅ Minimum age validator
export function ageValidator(minAge: number) {
  return (control: AbstractControl): { [key: string]: boolean } | null => {
    if (control.value) {
      const date = format(control.value, 'yyyy-MM-dd');
      const dob = parse(date, 'yyyy-MM-dd', new Date());
      const age = differenceInYears(new Date(), dob);

      if (age < minAge) {
        return { ageInvalid: true };
      }
    }
    return null;
  };
}

/**
 * Resolves a birthdate string to a Gregorian `Date` for comparison purposes only.
 * A year below 1700 is read as Hijri (`iYYYY-iMM-iDD`) — the stored value itself
 * is never converted, so whatever calendar the user picked is what gets sent.
 */
export function parseBirthdateForComparison(value: unknown): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const raw = String(value).trim();
  if (!raw) return null;

  const year = Number((raw.split('-')[0] || '').trim());
  if (!isNaN(year) && year > 0 && year < 1700) {
    const m = hijriMoment(raw, 'iYYYY-iM-iD');
    return m.isValid() ? m.toDate() : null;
  }

  const parsed = new Date(raw);
  return isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Birthdate must be at least 18 years ago and not in the future.
 * Accepts `YYYY-MM-DD` Gregorian and `iYYYY-iMM-iDD` Hijri strings.
 */
export function minAge18Validator(control: AbstractControl): ValidationErrors | null {
  const raw = control.value;
  if (raw === null || raw === undefined || raw === '') return null;
  const parsed = parseBirthdateForComparison(raw);
  if (!parsed) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dob = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());

  if (dob.getTime() > today.getTime()) return { future: true };

  const eighteenth = new Date(dob);
  eighteenth.setFullYear(eighteenth.getFullYear() + 18);
  if (eighteenth.getTime() > today.getTime()) return { minAge: true };

  return null;
}

/**
 * Full name in Arabic: only Arabic-script characters and spaces (no Latin digits/letters).
 * Allows main Arabic Unicode blocks + presentation forms (common on keyboards/OS).
 */
export const arabicFullNameScriptValidator: ValidatorFn = (
  c: AbstractControl
): ValidationErrors | null => {
  const raw = c.value;
  if (raw === null || raw === undefined) return null;
  const v = String(raw).trim();
  if (!v) return null;

  const arabicBlocks =
    /^[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF\s]+$/u;
  const hasArabicLetter =
    /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/.test(
      v
    );

  if (!hasArabicLetter) return { notArabic: true };
  if (!arabicBlocks.test(v)) return { notArabic: true };
  return null;
};

/** Arabic script ranges (reject in English full name). */
const ARABIC_SCRIPT =
  /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;

/**
 * Full name in English: Latin letters, spaces, and common name punctuation only
 * (apostrophe, hyphen). No Arabic script or digits.
 */
export const englishFullNameScriptValidator: ValidatorFn = (
  c: AbstractControl
): ValidationErrors | null => {
  const raw = c.value;
  if (raw === null || raw === undefined) return null;
  const v = String(raw).trim();
  if (!v) return null;

  if (ARABIC_SCRIPT.test(v)) return { notEnglish: true };
  if (/\d/.test(v)) return { notEnglish: true };

  const latinName =
    /^[a-zA-Z\u00C0-\u024F\u1E00-\u1EFF\s'.-]+$/u;
  if (!latinName.test(v)) return { notEnglish: true };

  const hasLatinLetter = /[a-zA-Z\u00C0-\u024F\u1E00-\u1EFF]/.test(v);
  if (!hasLatinLetter) return { notEnglish: true };

  return null;
};

/**
 * Full name must be exactly four space-separated words (NicInformation / portal policy).
 */
export const fourWordsFullNameValidator: ValidatorFn = (
  c: AbstractControl
): ValidationErrors | null => {
  const value = (c.value || '').toString().trim();
  if (!value) return null;
  const parts = value.split(/\s+/).filter((p: string) => p.length > 0);
  return parts.length === 4 ? null : { fourWords: true };
};

// ✅ Saudi NID (starts with 1 or 2, exactly 10 digits)
export const saudiNidValidator: ValidatorFn = (
  c: AbstractControl
): ValidationErrors | null => {
  const raw = c.value;
  const v = typeof raw === 'string' ? raw.trim() : '';
  const valid = /^(1|2)\d{9}$/.test(v); // ✅ matches exactly 10 digits
  return valid ? null : { saudiNid: true };
};

// ✅ Hijri date format YYYY-MM-DD or Date object
export const hijriDateValidator: ValidatorFn = (
  c: AbstractControl
): ValidationErrors | null => {
  const raw = c.value;

  // If it's a Date (e.g. from p-calendar)
  if (raw instanceof Date) {
    return null;
  }

  // If it's a string
  const v = typeof raw === 'string' ? raw.trim() : '';
  const ok = /^\d{4}-\d{2}-\d{2}$/.test(v);
  return ok ? null : { hijriDate: true };


  
};

export const saIbanValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value: string = control.value ? control.value.replace(/\s+/g, '') : ''; // remove spaces
  const valid = JariValidators.saPattern.test(value);
  return valid ? null : { saIbanInvalid: true };
};




export const IBAN_COUNTRIES: IbanCountry[] = [
  // Arab / MENA region (your original focus + more)
  { code: 'AE', length: 23, name: 'United Arab Emirates' },
  { code: 'BH', length: 22, name: 'Bahrain' },
  { code: 'EG', length: 27, name: 'Egypt' },
  { code: 'IQ', length: 23, name: 'Iraq' },
  { code: 'JO', length: 30, name: 'Jordan' },
  { code: 'KW', length: 30, name: 'Kuwait' },
  { code: 'LB', length: 28, name: 'Lebanon' },
  { code: 'OM', length: 23, name: 'Oman' },
  { code: 'PS', length: 29, name: 'State of Palestine' },
  { code: 'QA', length: 29, name: 'Qatar' },
  { code: 'SA', length: 24, name: 'Saudi Arabia' },

  // Europe / SEPA (most common)
  { code: 'AD', length: 24, name: 'Andorra', sepa: true },
  { code: 'AL', length: 28, name: 'Albania', sepa: true },
  { code: 'AT', length: 20, name: 'Austria', sepa: true },
  { code: 'AZ', length: 28, name: 'Azerbaijan', sepa: true },
  { code: 'BE', length: 16, name: 'Belgium', sepa: true },
  { code: 'BA', length: 20, name: 'Bosnia and Herzegovina' },
  { code: 'BG', length: 22, name: 'Bulgaria', sepa: true },
  { code: 'CH', length: 21, name: 'Switzerland', sepa: true },
  { code: 'CR', length: 22, name: 'Costa Rica' },
  { code: 'CY', length: 28, name: 'Cyprus', sepa: true },
  { code: 'CZ', length: 24, name: 'Czechia', sepa: true },
  { code: 'DE', length: 22, name: 'Germany', sepa: true },
  { code: 'DK', length: 18, name: 'Denmark', sepa: true },
  { code: 'DO', length: 28, name: 'Dominican Republic' },
  { code: 'EE', length: 20, name: 'Estonia', sepa: true },
  { code: 'ES', length: 24, name: 'Spain', sepa: true },
  { code: 'FI', length: 18, name: 'Finland', sepa: true },
  { code: 'FO', length: 18, name: 'Faroe Islands', sepa: true },
  { code: 'FR', length: 27, name: 'France', sepa: true },
  { code: 'GB', length: 22, name: 'United Kingdom', sepa: true },
  { code: 'GE', length: 22, name: 'Georgia' },
  { code: 'GI', length: 23, name: 'Gibraltar', sepa: true },
  { code: 'GL', length: 18, name: 'Greenland', sepa: true },
  { code: 'GR', length: 27, name: 'Greece', sepa: true },
  { code: 'HR', length: 21, name: 'Croatia', sepa: true },
  { code: 'HU', length: 28, name: 'Hungary', sepa: true },
  { code: 'IE', length: 22, name: 'Ireland', sepa: true },
  { code: 'IL', length: 23, name: 'Israel' },
  { code: 'IS', length: 26, name: 'Iceland', sepa: true },
  { code: 'IT', length: 27, name: 'Italy', sepa: true },
  { code: 'LI', length: 21, name: 'Liechtenstein', sepa: true },
  { code: 'LT', length: 20, name: 'Lithuania', sepa: true },
  { code: 'LU', length: 20, name: 'Luxembourg', sepa: true },
  { code: 'LV', length: 21, name: 'Latvia', sepa: true },
  { code: 'MC', length: 27, name: 'Monaco', sepa: true },
  { code: 'MD', length: 24, name: 'Moldova' },
  { code: 'ME', length: 22, name: 'Montenegro' },
  { code: 'MK', length: 19, name: 'North Macedonia' },
  { code: 'MT', length: 31, name: 'Malta', sepa: true },
  { code: 'MR', length: 27, name: 'Mauritania' },
  { code: 'MU', length: 30, name: 'Mauritius' },
  { code: 'NL', length: 18, name: 'Netherlands', sepa: true },
  { code: 'NO', length: 15, name: 'Norway', sepa: true },
  { code: 'PK', length: 24, name: 'Pakistan' },
  { code: 'PL', length: 26, name: 'Poland', sepa: true },
  { code: 'PT', length: 25, name: 'Portugal', sepa: true },
  { code: 'RO', length: 24, name: 'Romania', sepa: true },
  { code: 'RS', length: 22, name: 'Serbia' },
  { code: 'SC', length: 31, name: 'Seychelles' },
  { code: 'SE', length: 24, name: 'Sweden', sepa: true },
  { code: 'SI', length: 19, name: 'Slovenia', sepa: true },
  { code: 'SK', length: 24, name: 'Slovakia', sepa: true },
  { code: 'SM', length: 27, name: 'San Marino', sepa: true },
  { code: 'ST', length: 25, name: 'Sao Tome and Principe' },
  { code: 'TL', length: 23, name: 'Timor-Leste' },
  { code: 'TN', length: 24, name: 'Tunisia' },
  { code: 'TR', length: 26, name: 'Türkiye' },
  { code: 'VG', length: 24, name: 'British Virgin Islands' },
  { code: 'XK', length: 20, name: 'Kosovo' }, // provisional / in use

  // Others
  { code: 'AO', length: 25, name: 'Angola' },
  { code: 'BJ', length: 28, name: 'Benin' }, // in progress / partial
  { code: 'BR', length: 29, name: 'Brazil' },
  { code: 'BY', length: 28, name: 'Belarus' },
  { code: 'CI', length: 28, name: 'Côte d\'Ivoire' },
  { code: 'CM', length: 27, name: 'Cameroon' },
  { code: 'CV', length: 24, name: 'Cape Verde' },
  { code: 'DZ', length: 24, name: 'Algeria' }, // emerging
  { code: 'GT', length: 28, name: 'Guatemala' },
  { code: 'HN', length: 28, name: 'Honduras' },
  { code: 'IR', length: 26, name: 'Iran' }, // limited use
  { code: 'MG', length: 27, name: 'Madagascar' },
  { code: 'MZ', length: 25, name: 'Mozambique' },
  { code: 'SN', length: 28, name: 'Senegal' },
];

export const ibanValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  if (!control.value || typeof control.value !== 'string') {
    return null;
  }

  let iban = control.value.replace(/[\s-]/g, '').toUpperCase().trim();

  if (iban.length === 0) {
    return null;
  }

  // 1. Must start with 2 letters + 2 digits
  if (!/^[A-Z]{2}\d{2}/.test(iban)) {
    return { ibanInvalid: 'Must start with 2-letter country code followed by 2 digits' };
  }

  const countryCode = iban.substring(0, 2);
  const country = IBAN_COUNTRIES.find(c => c.code === countryCode);

  if (!country) {
    return { ibanInvalid: `Unsupported country code: ${countryCode}` };
  }

  // 2. Correct length
  if (iban.length !== country.length) {
    return {
      ibanInvalid: `Wrong length for ${country.name} (${country.code}): expected ${country.length}, got ${iban.length}`
    };
  }

  // 3. Allowed characters only
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(iban)) {
    return { ibanInvalid: 'Contains invalid characters (only A-Z, 0-9 allowed)' };
  }

  // ───────────────────────────────────────────────
  //               Checksum (MOD 97-10)
  // ───────────────────────────────────────────────

  const rearranged = iban.slice(4) + iban.slice(0, 4);
  let numeric = '';

  for (const char of rearranged) {
    numeric += /\d/.test(char)
      ? char
      : (char.charCodeAt(0) - 55).toString(); // A=10 ... Z=35
  }

  // Process in chunks to avoid huge number issues
  let remainder = 0;
  for (let i = 0; i < numeric.length; i += 9) {
    const chunk = numeric.slice(i, i + 9);
    remainder = (remainder * Math.pow(10, chunk.length) + Number(chunk)) % 97;
  }

  return remainder === 1
    ? null
    : { ibanInvalid: 'Invalid checksum (check digits incorrect)' };
};


// export const ibanValidator: ValidatorFn = (
//   control: AbstractControl
// ): ValidationErrors | null => {
//   if (!control.value) return null;

//   const iban = control.value.replace(/\s+/g, '').toUpperCase();

//   // Basic format check
//   if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/.test(iban)) {
//     return { ibanInvalid: true };
//   }

//   // Move first 4 chars to the end
//   const rearranged = iban.slice(4) + iban.slice(0, 4);

//   // Convert letters to numbers (A = 10, B = 11, ..., Z = 35)
//   let numericIban = '';
//   for (const char of rearranged) {
//     numericIban += isNaN(Number(char))
//       ? (char.charCodeAt(0) - 55).toString()
//       : char;
//   }

//   // MOD 97 check
//   let remainder = 0;
//   for (let i = 0; i < numericIban.length; i += 9) {
//     remainder = Number(remainder + numericIban.substr(i, 9)) % 97;
//   }

//   return remainder === 1 ? null : { ibanInvalid: true };
// };

export const swiftCodeValidator: ValidatorFn = (
  control: AbstractControl
): ValidationErrors | null => {
  if (!control.value) return null;

  const value = control.value.trim().toUpperCase();

  const swiftRegex = /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/;

  return swiftRegex.test(value)
    ? null
    : { swiftInvalid: true };
};